import { createAsyncThunk } from '@reduxjs/toolkit';
import RootState from "../states/root.state";
import {
  getMyApiKeys,
  generateMyApiKey as generateMyApiKeyApi,
  revokeMyApiKey as revokeMyApiKeyApi,
  getAllApiKeys,
  generateApiKeyAdmin,
  revokeApiKeyAdmin,
} from '../../services/api/apiKeys.api';

/** Laravel's paginator payload → the {data, meta} shape the table components expect. */
const normalizePaginator = (payload: any) => ({
  data: payload?.data ?? [],
  meta: {
    total_pages: payload?.last_page ?? 1,
    total: payload?.total ?? 0,
    current_page: payload?.current_page ?? 1,
    per_page: payload?.per_page ?? 0,
  },
});

/* ----------------------------- My API Keys ----------------------------- */

export const fetchMyApiKeys = createAsyncThunk<any, void, { rejectValue: string; state: RootState }>(
  'myApiKeys/list',
  async (_arg, { rejectWithValue, getState }) => {
    try {
      const { user } = getState();
      const response = await getMyApiKeys(user.accessToken);
      return { data: response.data, meta: null };
    } catch (error: any) {
      if (error.response) {
        return rejectWithValue(error.response.data.data?.message ?? error.response.data.message ?? "An error occurred while fetching your API keys.");
      }
      return rejectWithValue('An error occurred while fetching your API keys.');
    }
  }
);

export const generateMyApiKey = createAsyncThunk<any, { spaGuid: string }, { rejectValue: string; state: RootState }>(
  'myApiKeys/generate',
  async ({ spaGuid }, { rejectWithValue, getState }) => {
    try {
      const { user } = getState();
      const response = await generateMyApiKeyApi(user.accessToken, spaGuid);
      return response.data; // { spaGuid, apiKey }
    } catch (error: any) {
      if (error.response) {
        return rejectWithValue(error.response.data.data?.message ?? error.response.data.message ?? "An error occurred while generating the API key.");
      }
      return rejectWithValue('An error occurred while generating the API key.');
    }
  }
);

export const revokeMyApiKey = createAsyncThunk<any, { spaGuid: string }, { rejectValue: string; state: RootState }>(
  'myApiKeys/revoke',
  async ({ spaGuid }, { rejectWithValue, getState }) => {
    try {
      const { user } = getState();
      const response = await revokeMyApiKeyApi(user.accessToken, spaGuid);
      return response.data; // { spaGuid, revoked }
    } catch (error: any) {
      if (error.response) {
        return rejectWithValue(error.response.data.data?.message ?? error.response.data.message ?? "An error occurred while revoking the API key.");
      }
      return rejectWithValue('An error occurred while revoking the API key.');
    }
  }
);

/* --------------------------- Manage API Keys --------------------------- */

export const fetchManageApiKeys = createAsyncThunk<any, string, { rejectValue: string; state: RootState }>(
  'manageApiKeys/list',
  async (params, { rejectWithValue, getState }) => {
    try {
      const { user } = getState();
      const response = await getAllApiKeys(user.accessToken, params);
      return normalizePaginator(response);
    } catch (error: any) {
      if (error.response) {
        return rejectWithValue(error.response.data.data?.message ?? error.response.data.message ?? "An error occurred while fetching API keys.");
      }
      return rejectWithValue('An error occurred while fetching API keys.');
    }
  }
);

export const generateManageApiKey = createAsyncThunk<any, { spaGuid: string, params: string }, { rejectValue: string; state: RootState }>(
  'manageApiKeys/generate',
  async ({ spaGuid, params }, { rejectWithValue, getState }) => {
    try {
      const { user } = getState();
      const created = await generateApiKeyAdmin(user.accessToken, spaGuid);
      // Refetch so the new row arrives fully enriched (spa/user detail).
      const list = await getAllApiKeys(user.accessToken, params);
      return { apiKey: created.data?.apiKey, ...normalizePaginator(list) };
    } catch (error: any) {
      if (error.response) {
        return rejectWithValue(error.response.data.data?.message ?? error.response.data.message ?? "An error occurred while generating the API key.");
      }
      return rejectWithValue('An error occurred while generating the API key.');
    }
  }
);

export const revokeManageApiKey = createAsyncThunk<any, { apikey: string, params: string }, { rejectValue: string; state: RootState }>(
  'manageApiKeys/revoke',
  async ({ apikey, params }, { rejectWithValue, getState }) => {
    try {
      const { user } = getState();
      await revokeApiKeyAdmin(user.accessToken, apikey);
      const list = await getAllApiKeys(user.accessToken, params);
      return normalizePaginator(list);
    } catch (error: any) {
      if (error.response) {
        return rejectWithValue(error.response.data.data?.message ?? error.response.data.message ?? "An error occurred while revoking the API key.");
      }
      return rejectWithValue('An error occurred while revoking the API key.');
    }
  }
);
